import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import './LegalPage.css';

function PrivacyPage() {
  return (
    <div className="legal-page">
      <Navbar />

      <div className="legal-container">
        <div className="legal-header">
          <Link to="/" className="legal-back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M19 12H5m7-7l-7 7 7 7"/>
            </svg>
            Back to Home
          </Link>
          <h1>Privacy Policy</h1>
          <p className="legal-updated">Last updated: March 2026</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Mindmark ("we", "our", or "us") is committed to protecting your privacy. This Privacy
              Policy explains how we collect, use, disclose, and safeguard your information when you
              use our Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>

            <h3>Account Information</h3>
            <p>When you create an account, we collect:</p>
            <ul>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Account preferences</li>
            </ul>

            <h3>Content You Submit</h3>
            <p>When you use the Service, we may process:</p>
            <ul>
              <li>Text content submitted for compliance checking</li>
              <li>URLs submitted for analysis</li>
              <li>Documents uploaded for review</li>
              <li>Questions and conversations submitted through Compliance Chat</li>
            </ul>

            <h3>Usage Information</h3>
            <p>We automatically collect:</p>
            <ul>
              <li>Device and browser information</li>
              <li>IP address</li>
              <li>Usage patterns and feature interactions</li>
              <li>Date and time of access</li>
            </ul>

            <h3>Payment Information</h3>
            <p>
              Payment processing is handled by Stripe. We do not store your full credit card number.
              We receive limited information from Stripe including the last four digits of your card
              and transaction status.
            </p>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use collected information to:</p>
            <ul>
              <li>Provide and maintain the Service</li>
              <li>Process your compliance checks</li>
              <li>Manage your account and subscription</li>
              <li>Send service-related communications</li>
              <li>Improve and develop the Service</li>
              <li>Detect and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Data Storage and Security</h2>
            <p>
              Your account data is stored securely using Supabase, which employs industry-standard
              security measures including encryption at rest and in transit.
            </p>
            <p>
              Content submitted for compliance checks (text, URLs, and documents) is processed in
              real-time and is not stored on our servers. Your check history is kept locally in your
              browser's localStorage to help uphold client confidentiality. This data remains on
              your device and under your control.
            </p>
            <p>
              Documents uploaded to the Document Repository are stored securely in Supabase Storage.
              Each user's files are isolated in private, per-user storage that is only accessible by
              the account owner. These files are encrypted at rest and in transit. Documents stored in
              the Repository are not shared with third parties, not used for AI training, and are
              permanently deleted when you delete your account or remove individual files.
            </p>
            <p>
              Compliance Chat conversations are stored securely on our servers to maintain context
              across sessions and allow you to revisit previous conversations. You can delete any
              chat session at any time from the sidebar.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Data Sharing</h2>
            <p>We may share your information with:</p>
            <ul>
              <li>
                <strong>Service Providers:</strong> Third parties that help us operate the Service,
                including Supabase (authentication and database), Stripe (payments), OpenAI (AI
                analysis), Resend (email delivery), and Jina (website content extraction).
              </li>
              <li>
                <strong>Legal Requirements:</strong> When required by law, court order, or government
                request.
              </li>
              <li>
                <strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale
                of assets.
              </li>
            </ul>
            <p>
              We do not sell your personal information to third parties.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access the personal information we hold about you</li>
              <li>Correct inaccurate or incomplete information</li>
              <li>Delete your account and associated data</li>
              <li>Export your data in a portable format</li>
              <li>Withdraw consent for optional data processing</li>
              <li>Lodge a complaint with a data protection authority</li>
            </ul>
            <p>
              To exercise these rights, please contact us at{' '}
              <a href="mailto:privacy@mindmark.au">privacy@mindmark.au</a>
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Cookies and Tracking</h2>
            <p>
              We use essential cookies to maintain your session and authentication state. We do not
              use tracking cookies or third-party analytics that follow you across websites.
            </p>
            <p>
              Local storage is used to save your preferences and check history on your device.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention</h2>
            <p>
              We retain your account information for as long as your account is active. When you
              delete your account, we will delete your personal information within 30 days, except
              where we are required to retain it for legal or regulatory purposes.
            </p>
            <p>
              Documents stored in the Document Repository are deleted immediately when you remove
              them, and all stored documents are permanently deleted within 30 days of account
              deletion.
            </p>
            <p>
              Local browser data (history, preferences) is controlled by you and can be cleared
              through your browser settings.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. International Transfers</h2>
            <p>
              Your information may be processed in countries other than Australia. We ensure that
              any international transfers comply with applicable data protection laws and that
              adequate safeguards are in place.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Children's Privacy</h2>
            <p>
              The Service is not intended for users under 18 years of age. We do not knowingly
              collect information from children. If you believe we have collected information from
              a child, please contact us immediately.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of significant
              changes by email or through the Service. Your continued use of the Service after changes
              constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="legal-section">
            <h2>12. Contact Us</h2>
            <p>
              For questions about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p>
              Email: <a href="mailto:privacy@mindmark.au">privacy@mindmark.au</a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PrivacyPage;
